import {
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  makeStyles,
} from "@material-ui/core";
import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import UnfoldMoreIcon from "@material-ui/icons/UnfoldMore";
import UnfoldLessIcon from "@material-ui/icons/UnfoldLess";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import StyledTableRow from "../../components/StyledTableRow";
import { getUniqueValues, countOrACVFormatter } from "../../util/customFunctions";
import CopyTablesObject from "../../components/xlsx-export/CopyTablesObject";
import stickytableStyle from "../../StickyTableStyles/Sticky_Table_Styles";

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "15rem",
    fontSize: "1rem",
    color: "black",
    textAlign: "center",
    background: "white",
    border: "0.5px solid lightgrey",
  },
});

const useStyle = makeStyles(() => ({
  repHeader: {
    fontWeight: 600,
    border: "0.5px solid lightgrey",
    padding: "5px",
    color: "inherit",
    fontSize: "0.85rem",
    position: "relative",
    // No whiteSpace here — let width control wrapping
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  datacell: {
    border: "0.5px solid lightgrey",
    padding: "5px",
    fontSize: "0.85rem",
    // No whiteSpace: nowrap here — wrapping is controlled per-cell via inline style
    overflow: "hidden",
  },
  content: { width: "100%" },
  resizeHandle: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: 5,
    cursor: "col-resize",
    zIndex: 10,
    "&:hover": {
      background: "rgba(0,0,0,0.2)",
    },
  },
}));

function SortHeader({ label, field, sortConfig, onSort }) {
  const active = sortConfig.field === field;
  const icon = active
    ? sortConfig.direction === "asc"
      ? <ArrowUpwardIcon style={{ fontSize: 14, marginLeft: 2, verticalAlign: "middle" }} />
      : <ArrowDownwardIcon style={{ fontSize: 14, marginLeft: 2, verticalAlign: "middle" }} />
    : null;
  return (
    <span
      style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", userSelect: "none" }}
      onClick={() => onSort(field)}
    >
      {label}
      {icon}
    </span>
  );
}

export default function TeamAttainmentTable({ data, handleChangeLocation, Value_Label, toggleValue }) {
  const classes = useStyle();
  const stickyClasses = stickytableStyle();
  const tableRef = React.createRef();
  const tableContainerRef = useRef();
  const tableWrapperRef = useRef(); // wraps both copy button + table container

  const [isSticky, setIsSticky] = useState(false);
  const [tableWidth, setTableWidth] = useState(undefined);
  const [parentWidth, setParentWidth] = useState(undefined);
  const [rowPin, setRowPin] = useState(false);
  const [sortConfig, setSortConfig] = useState({ field: null, direction: "asc" });
  // Reset column sort when toggle changes
  const prevToggle = useRef(toggleValue);
  useEffect(() => {
    if (prevToggle.current !== toggleValue) {
      setSortConfig({ field: null, direction: "asc" });
      prevToggle.current = toggleValue;
    }
  }, [toggleValue]);
  const [colWidths, setColWidths] = useState({ contribOverall: 100, contribParent: 100 });
  const headerRow1Ref = useRef(null);
  const [headerRow1Height, setHeaderRow1Height] = useState(0);

  useEffect(() => {
    if (headerRow1Ref.current) {
      setHeaderRow1Height(headerRow1Ref.current.getBoundingClientRect().height);
    }
  });

  // Track actual rendered table width for copy button alignment
  useEffect(() => {
    const updateTableWidth = () => {
      if (tableRef.current) {
        setTableWidth(tableRef.current.getBoundingClientRect().width);
      }
    };
    // Use ResizeObserver to track table width changes (including after column resize)
    const ro = new ResizeObserver(updateTableWidth);
    if (tableRef.current) ro.observe(tableRef.current);
    updateTableWidth();
    return () => ro.disconnect();
  }, [tableRef]);

  const final = data && data.final ? data.final : [];
  const rowTotal = data && data.rowTotal ? data.rowTotal : [];
  const total = data && data.total ? data.total : [];
  const teamDisplayOrder = data && data.teamDisplayOrder ? data.teamDisplayOrder : [];
  const teamDisplayOrderAlpha = data && data.teamDisplayOrderAlpha ? data.teamDisplayOrderAlpha : [];

  const uniqueQuarters = useMemo(
    () => final.map((item) => item.Quarter).filter(getUniqueValues).sort(),
    [final]
  );

  const activeDisplayOrder = toggleValue === "AlphaSort" ? teamDisplayOrderAlpha : teamDisplayOrder;

  const orderedTeams = useMemo(() => {
    if (activeDisplayOrder && activeDisplayOrder.length > 0) return activeDisplayOrder;
    return rowTotal
      .slice()
      .sort((a, b) => (a.attainmentPercent > b.attainmentPercent ? -1 : 1))
      .map((item) => ({ name: item.Display_Name, indent: 0, isParent: false, parentValue: null }));
  }, [activeDisplayOrder, rowTotal]);

  const [expandedTeams, setExpandedTeams] = useState(() => new Set());

  const handleExpandClick = useCallback((teamName) => {
    setExpandedTeams((prev) => {
      const next = new Set(prev);
      if (next.has(teamName)) next.delete(teamName); else next.add(teamName);
      return next;
    });
  }, []);

  const handleExpandAll = useCallback(() => {
    setExpandedTeams(new Set(orderedTeams.filter(t => t.isParent).map(t => t.name)));
  }, [orderedTeams]);

  const handleCollapseAll = useCallback(() => {
    setExpandedTeams(new Set());
  }, []);

  const visibleTeams = useMemo(() => {
    return orderedTeams.filter((teamInfo) => {
      let parent = teamInfo.parentValue;
      while (parent) {
        const parentRow = orderedTeams.find((r) => r.name === parent);
        if (!parentRow) break;
        if (!expandedTeams.has(parent)) return false;
        parent = parentRow.parentValue;
      }
      return true;
    });
  }, [orderedTeams, expandedTeams]);

  const rowTotalMap = useMemo(() => {
    const m = {};
    rowTotal.forEach((r) => { m[r.Display_Name] = r; });
    return m;
  }, [rowTotal]);

  const quarterDataMap = useMemo(() => {
    const m = {};
    final.forEach((r) => {
      if (!m[r.Display_Name]) m[r.Display_Name] = {};
      m[r.Display_Name][r.Quarter] = r;
    });
    return m;
  }, [final]);

  const parentRowColors = ["#c5d9f1", "#dae9f8", "#eaf3fb"];

  const handleSort = useCallback((field) => {
    setSortConfig((prev) => {
      if (prev.field === field) return { field, direction: prev.direction === "asc" ? "desc" : "asc" };
      return { field, direction: "asc" };
    });
  }, []);

  // Column resize handler — no minimum width, allows shrink to character width
  const onResizeMouseDown = useCallback((e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const th = e.target.closest("th");
    const startWidth = th ? th.getBoundingClientRect().width : 60;
    const onMouseMove = (moveEvent) => {
      const diff = moveEvent.clientX - startX;
      const newWidth = Math.max(8, startWidth + diff); // allow shrink to ~1 char (8px)
      setColWidths((prev) => ({ ...prev, [colKey]: newWidth }));
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, []);

  const resizeHandle = (colKey) => (
    <div className={classes.resizeHandle} onMouseDown={(e) => onResizeMouseDown(e, colKey)} />
  );

  // Returns width style for a header cell:
  // - If user has resized: use exact px width (enables wrapping when shrunk)
  // - If not resized: no width/minWidth set, so browser fits to content naturally
  const colStyle = (colKey) => {
    if (colWidths[colKey]) {
      return {
        width: colWidths[colKey],
        maxWidth: colWidths[colKey],
        whiteSpace: "normal",   // allow wrap when manually shrunk
        wordBreak: "break-word",
        overflow: "hidden",
      };
    }
    // No width set — browser auto-fits to content, no wrapping
    return { whiteSpace: "nowrap" };
  };

  // Same for data cells — wraps only when column has been manually resized
  const dataCellStyle = (colKey, extra = {}) => {
    if (colWidths[colKey]) {
      return {
        ...extra,
        width: colWidths[colKey],
        maxWidth: colWidths[colKey],
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflow: "hidden",
      };
    }
    return { ...extra, whiteSpace: "nowrap" };
  };

  const sortedVisibleTeams = useMemo(() => {
    if (!sortConfig.field) return visibleTeams;
    const { field, direction } = sortConfig;
    return [...visibleTeams].sort((a, b) => {
      const rowA = rowTotalMap[a.name];
      const rowB = rowTotalMap[b.name];
      if (!rowA || !rowB) return 0;

      let valA, valB;
      if (field.startsWith("quota_")) {
        const q = field.replace("quota_", "");
        valA = quarterDataMap[a.name]?.[q]?.Quota ?? 0;
        valB = quarterDataMap[b.name]?.[q]?.Quota ?? 0;
      } else if (field.startsWith("acv_")) {
        const q = field.replace("acv_", "");
        valA = quarterDataMap[a.name]?.[q]?.acv ?? 0;
        valB = quarterDataMap[b.name]?.[q]?.acv ?? 0;
      } else if (field.startsWith("attain_")) {
        const q = field.replace("attain_", "");
        valA = quarterDataMap[a.name]?.[q]?.attainmentPercent ?? 0;
        valB = quarterDataMap[b.name]?.[q]?.attainmentPercent ?? 0;
      } else if (field === "Quota") {
        valA = rowA.Quota ?? 0;
        valB = rowB.Quota ?? 0;
      } else if (field === "acv") {
        valA = rowA.acv ?? 0;
        valB = rowB.acv ?? 0;
      } else if (field === "attainmentPercent") {
        valA = rowA.attainmentPercent ?? 0;
        valB = rowB.attainmentPercent ?? 0;
      } else if (field === "contribOverall") {
        valA = rowA.contributionToToalPercent ?? 0;
        valB = rowB.contributionToToalPercent ?? 0;
      } else if (field === "contribParent") {
        valA = rowA.contributionToParentPercent ?? 0;
        valB = rowB.contributionToParentPercent ?? 0;
      } else {
        return 0;
      }
      return direction === "asc" ? valA - valB : valB - valA;
    });
  }, [visibleTeams, sortConfig, rowTotalMap, quarterDataMap]);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const handleScroll = () => {
      setIsSticky(el.scrollLeft > 0);
      setRowPin(el.scrollTop > 0);
    };
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  // Track container width for copy button positioning
  useEffect(() => {
    const updateParentWidth = () => {
      if (tableContainerRef.current) {
        setParentWidth(tableContainerRef.current.getBoundingClientRect().width);
      }
    };
    updateParentWidth();
    window.addEventListener("resize", updateParentWidth);
    return () => window.removeEventListener("resize", updateParentWidth);
  }, []);

  if (!data || !data.final || !data.rowTotal) return null;

  const fmt = (val) => (val == null || isNaN(val) ? "-" : countOrACVFormatter(val, true));
  const fmtPct = (val) => (val == null || isNaN(val) ? "-" : `${Math.round(val)}%`);
  const fmtPct1 = (val) => (val == null || isNaN(val) ? "-" : `${val.toFixed(1)}%`);

  const grandTotal = total.length > 0 ? total[0] : {};

  return (
    <div style={{ height: "100%", display: "flex", alignItems: "center", width: "100%", justifyContent: "center" }}>
      <CardContent className={classes.content} style={{ padding: 10 }}>
        {parentWidth !== undefined ? (
          <div
            style={{
              display: "flex",
              justifyContent: "right",
              marginBottom: "0.6rem",
              width: `${parentWidth}px`,
            }}
          >
            <CopyTablesObject tableRef={tableRef} />
          </div>
        ) : null}
        <Grid container justify="center">
          <TableContainer
            style={{
              width: "auto",
              borderLeft: isSticky ? "0.5px solid lightgrey" : "",
              borderTop: rowPin ? "0.5px solid lightgrey" : "",
              maxHeight: "85vh",
            }}
            ref={tableContainerRef}
          >
            <Table
              ref={tableRef}
              size="small"
              aria-label="team attainment table"
              style={{ tableLayout: "fixed", width: "auto" }}
            >
              <TableHead>
                {/* Row 1: Quarter group headers */}
                <TableRow ref={headerRow1Ref} style={{ fontWeight: 600, outline: "0.4px solid lightgrey", outlineOffset: "-0.5px" }}>
                  <TableCell
                    align="center"
                    className={isSticky ? `${stickyClasses.sticky} ${classes.repHeader}` : classes.repHeader}
                    colSpan={1}
                    style={{
                      background: "white",
                      position: "sticky",
                      top: -1,
                      zIndex: 6,
                      ...colStyle("team"),
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 4 }}>
                      <span>Team</span>
                      <KeyboardArrowDownIcon onClick={handleExpandAll} style={{ color: "gray", cursor: "pointer", fontSize: 18 }} />
                      <KeyboardArrowUpIcon onClick={handleCollapseAll} style={{ color: "gray", cursor: "pointer", fontSize: 18 }} />
                    </div>
                    {resizeHandle("team")}
                  </TableCell>
                  {uniqueQuarters.map((q, i) => (
                    <TableCell
                      key={q}
                      align="center"
                      className={classes.repHeader}
                      colSpan={3}
                      style={i % 2 === 0
                        ? { background: "#4471c4", color: "white", position: "sticky", top: -1, zIndex: 5, whiteSpace: "nowrap" }
                        : { background: "#5b9bd5", color: "white", position: "sticky", top: -1, zIndex: 5, whiteSpace: "nowrap" }}
                    >
                      {q}
                    </TableCell>
                  ))}
                  {uniqueQuarters.length > 1 && (
                    <TableCell
                      align="center"
                      className={classes.repHeader}
                      colSpan={3}
                      style={uniqueQuarters.length % 2 === 0
                        ? { background: "#4471c4", color: "white", position: "sticky", top: -1, zIndex: 5, whiteSpace: "nowrap" }
                        : { background: "#5b9bd5", color: "white", position: "sticky", top: -1, zIndex: 5, whiteSpace: "nowrap" }}
                    >
                      Total
                    </TableCell>
                  )}
                  <TableCell
                    align="center"
                    className={classes.repHeader}
                    rowSpan={2}
                    style={{
                      background: "#4472c4",
                      color: "white",
                      position: "sticky",
                      top: -1,
                      zIndex: 5,
                      minWidth: 100,
                      ...colStyle("contribOverall"),
                    }}
                  >
                    <SortHeader label="Contribution to Overall Total %" field="contribOverall" sortConfig={sortConfig} onSort={handleSort} />
                    {resizeHandle("contribOverall")}
                  </TableCell>
                  <TableCell
                    align="center"
                    className={classes.repHeader}
                    rowSpan={2}
                    style={{
                      background: "#4472c4",
                      color: "white",
                      position: "sticky",
                      top: -1,
                      zIndex: 5,
                      minWidth: 100,
                      ...colStyle("contribParent"),
                    }}
                  >
                    <SortHeader label="Contribution to Parent Total %" field="contribParent" sortConfig={sortConfig} onSort={handleSort} />
                    {resizeHandle("contribParent")}
                  </TableCell>
                </TableRow>

                {/* Row 2: Sub-headers */}
                <TableRow style={{ fontWeight: 600, outline: "0.4px solid lightgrey", outlineOffset: "-0.5px" }}>
                  <TableCell
                    align="center"
                    className={isSticky ? `${stickyClasses.sticky} ${classes.repHeader}` : classes.repHeader}
                    style={{ background: "white", position: "sticky", top: headerRow1Height - 1, zIndex: 6 }}
                  />
                  {uniqueQuarters.map((q) => (
                    <React.Fragment key={q}>
                      <TableCell
                        align="center"
                        className={classes.repHeader}
                        style={{ background: "white", position: "sticky", top: headerRow1Height - 1, zIndex: 5, ...colStyle(`quota_${q}`) }}
                      >
                        <SortHeader label="Quota" field={`quota_${q}`} sortConfig={sortConfig} onSort={handleSort} />
                        {resizeHandle(`quota_${q}`)}
                      </TableCell>
                      <TableCell
                        align="center"
                        className={classes.repHeader}
                        style={{ background: "white", position: "sticky", top: headerRow1Height - 1, zIndex: 5, ...colStyle(`acv_${q}`) }}
                      >
                        <SortHeader label="Actual" field={`acv_${q}`} sortConfig={sortConfig} onSort={handleSort} />
                        {resizeHandle(`acv_${q}`)}
                      </TableCell>
                      <TableCell
                        align="center"
                        className={classes.repHeader}
                        style={{ background: "white", position: "sticky", top: headerRow1Height - 1, zIndex: 5, ...colStyle(`attain_${q}`) }}
                      >
                        <SortHeader label="Attain %" field={`attain_${q}`} sortConfig={sortConfig} onSort={handleSort} />
                        {resizeHandle(`attain_${q}`)}
                      </TableCell>
                    </React.Fragment>
                  ))}
                  {uniqueQuarters.length > 1 && (
                    <>
                      <TableCell
                        align="center"
                        className={classes.repHeader}
                        style={{ background: "white", position: "sticky", top: headerRow1Height - 1, zIndex: 5, ...colStyle("Quota") }}
                      >
                        <SortHeader label="Quota" field="Quota" sortConfig={sortConfig} onSort={handleSort} />
                        {resizeHandle("Quota")}
                      </TableCell>
                      <TableCell
                        align="center"
                        className={classes.repHeader}
                        style={{ background: "white", position: "sticky", top: headerRow1Height - 1, zIndex: 5, ...colStyle("acv") }}
                      >
                        <SortHeader label="Actual" field="acv" sortConfig={sortConfig} onSort={handleSort} />
                        {resizeHandle("acv")}
                      </TableCell>
                      <TableCell
                        align="center"
                        className={classes.repHeader}
                        style={{ background: "white", position: "sticky", top: headerRow1Height - 1, zIndex: 5, ...colStyle("attainmentPercent") }}
                      >
                        <SortHeader label="Attain %" field="attainmentPercent" sortConfig={sortConfig} onSort={handleSort} />
                        {resizeHandle("attainmentPercent")}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedVisibleTeams.map((teamInfo, idx) => {
                  const row = rowTotalMap[teamInfo.name];
                  if (!row) return null;
                  const bg = teamInfo.isParent
                    ? parentRowColors[Math.min(teamInfo.indent - 1, parentRowColors.length - 1)] || parentRowColors[0]
                    : undefined;
                  const rowBg = bg || ((idx + 1) % 2 === 0 ? "#f5f5f5" : "white");

                  return (
                    <StyledTableRow key={teamInfo.name} style={bg ? { background: bg } : undefined}>
                      <TableCell
                        align="left"
                        className={isSticky ? `${stickyClasses.sticky} ${classes.datacell}` : classes.datacell}
                        style={{
                          background: rowBg,
                          paddingLeft: `${(teamInfo.indent || 0) * 8 + 5}px`,
                          fontWeight: teamInfo.isParent ? 600 : "inherit",
                          cursor: teamInfo.isParent ? "pointer" : "default",
                          ...dataCellStyle("team"),
                        }}
                        onClick={() => teamInfo.isParent && handleExpandClick(teamInfo.name)}
                      >
                        <span style={{ display: "flex", alignItems: "center", gap: 2 }}>
                          {teamInfo.isParent ? (
                            expandedTeams.has(teamInfo.name)
                              ? <KeyboardArrowUpIcon style={{ fontSize: 18, color: "gray" }} />
                              : <KeyboardArrowDownIcon style={{ fontSize: 18, color: "gray" }} />
                          ) : <span style={{ width: 18, minWidth: 18 }} />}
                          {teamInfo.name}
                        </span>
                      </TableCell>
                      {uniqueQuarters.map((q) => {
                        const qData = quarterDataMap[teamInfo.name]?.[q];
                        return (
                          <React.Fragment key={q}>
                            <TableCell
                              align="right"
                              className={classes.datacell}
                              style={dataCellStyle(`quota_${q}`, { background: bg, fontWeight: teamInfo.isParent ? 600 : "inherit" })}
                            >
                              {fmt(qData?.Quota)}
                            </TableCell>
                            <TableCell
                              align="right"
                              className={classes.datacell}
                              style={dataCellStyle(`acv_${q}`, { background: bg, fontWeight: teamInfo.isParent ? 600 : "inherit", cursor: "pointer" })}
                              onClick={() => handleChangeLocation("drilldownOnGraph", [q], [teamInfo.name])}
                            >
                              {fmt(qData?.acv)}
                            </TableCell>
                            <TableCell
                              align="right"
                              className={classes.datacell}
                              style={dataCellStyle(`attain_${q}`, { background: bg, fontWeight: teamInfo.isParent ? 600 : "inherit" })}
                            >
                              {fmtPct(qData?.attainmentPercent)}
                            </TableCell>
                          </React.Fragment>
                        );
                      })}
                      {uniqueQuarters.length > 1 && (
                        <>
                          <TableCell
                            align="right"
                            className={classes.datacell}
                            style={dataCellStyle("Quota", { background: bg, fontWeight: teamInfo.isParent ? 600 : "inherit" })}
                          >
                            {fmt(row.Quota)}
                          </TableCell>
                          <TableCell
                            align="right"
                            className={classes.datacell}
                            style={dataCellStyle("acv", { background: bg, fontWeight: teamInfo.isParent ? 600 : "inherit", cursor: "pointer" })}
                            onClick={() => handleChangeLocation("drilldownOnGraph", uniqueQuarters, [teamInfo.name])}
                          >
                            {fmt(row.acv)}
                          </TableCell>
                          <TableCell
                            align="right"
                            className={classes.datacell}
                            style={dataCellStyle("attainmentPercent", { background: bg, fontWeight: teamInfo.isParent ? 600 : "inherit" })}
                          >
                            {fmtPct(row.attainmentPercent)}
                          </TableCell>
                        </>
                      )}
                      <TableCell
                        align="right"
                        className={classes.datacell}
                        style={dataCellStyle("contribOverall", { background: bg, fontWeight: teamInfo.isParent ? 600 : "inherit" })}
                      >
                        {fmtPct1(row.contributionToToalPercent)}
                      </TableCell>
                      <TableCell
                        align="right"
                        className={classes.datacell}
                        style={dataCellStyle("contribParent", { background: bg, fontWeight: teamInfo.isParent ? 600 : "inherit" })}
                      >
                        {fmtPct1(row.contributionToParentPercent)}
                      </TableCell>
                    </StyledTableRow>
                  );
                })}

                {/* Total row */}
                <StyledTableRow style={{ background: "#f5f5f5" }}>
                  <TableCell
                    align="left"
                    className={isSticky ? `${stickyClasses.sticky} ${classes.datacell}` : classes.datacell}
                    style={dataCellStyle("team", { background: "#f5f5f5", fontWeight: 700 })}
                  >
                    Total
                  </TableCell>
                  {uniqueQuarters.map((q) => {
                    const colTotal = data.columnTotal?.find((c) => c.Quarter === q);
                    return (
                      <React.Fragment key={q}>
                        <TableCell
                          align="right"
                          className={classes.datacell}
                          style={dataCellStyle(`quota_${q}`, { background: "#f5f5f5", fontWeight: 700 })}
                        >
                          {fmt(colTotal?.Quota)}
                        </TableCell>
                        <TableCell
                          align="right"
                          className={classes.datacell}
                          style={dataCellStyle(`acv_${q}`, { background: "#f5f5f5", fontWeight: 700, cursor: "pointer" })}
                          onClick={() => handleChangeLocation("drilldownOnGraph", [q], orderedTeams.map((t) => t.name))}
                        >
                          {fmt(colTotal?.acv)}
                        </TableCell>
                        <TableCell
                          align="right"
                          className={classes.datacell}
                          style={dataCellStyle(`attain_${q}`, { background: "#f5f5f5", fontWeight: 700 })}
                        >
                          {fmtPct(colTotal?.attainmentPercent)}
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  {uniqueQuarters.length > 1 && (
                    <>
                      <TableCell
                        align="right"
                        className={classes.datacell}
                        style={dataCellStyle("Quota", { background: "#f5f5f5", fontWeight: 700 })}
                      >
                        {fmt(grandTotal.Quota)}
                      </TableCell>
                      <TableCell
                        align="right"
                        className={classes.datacell}
                        style={dataCellStyle("acv", { background: "#f5f5f5", fontWeight: 700, cursor: "pointer" })}
                        onClick={() => handleChangeLocation("drilldownOnGraph", uniqueQuarters, orderedTeams.map((t) => t.name))}
                      >
                        {fmt(grandTotal.acv)}
                      </TableCell>
                      <TableCell
                        align="right"
                        className={classes.datacell}
                        style={dataCellStyle("attainmentPercent", { background: "#f5f5f5", fontWeight: 700 })}
                      >
                        {fmtPct(grandTotal.attainmentPercent)}
                      </TableCell>
                    </>
                  )}
                  <TableCell
                    align="center"
                    className={classes.datacell}
                    style={dataCellStyle("contribOverall", { background: "#f5f5f5", fontWeight: 700 })}
                  >-</TableCell>
                  <TableCell
                    align="center"
                    className={classes.datacell}
                    style={dataCellStyle("contribParent", { background: "#f5f5f5", fontWeight: 700 })}
                  >-</TableCell>
                </StyledTableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </CardContent>
    </div>
  );
}